import {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';
import { environment } from '../../../environments/environment';

/**
 * MSAL Logger callback for debugging
 */
export function loggerCallback(logLevel: LogLevel, message: string): void {
  if (!environment.production) {
    console.log(`[MSAL] ${message}`);
  }
}

// Create MSAL instance once and reuse
let msalInstance: PublicClientApplication | null = null;

/**
 * Creates and configures the MSAL PublicClientApplication instance
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  if (!msalInstance) {
    // Use the current window origin so MSAL always redirects back to the
    // SAME domain the user came in on (either the SWA default URL or any
    // custom domain). This prevents cross-domain sessionStorage loss which
    // causes "No se recibió respuesta de Microsoft" errors.
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const redirectUri = origin
      ? `${origin}/auth/microsoft/callback`
      : environment.msalConfig.auth.redirectUri;
    const postLogoutRedirectUri = origin
      ? `${origin}/auth/login`
      : environment.msalConfig.auth.postLogoutRedirectUri;

    msalInstance = new PublicClientApplication({
      auth: {
        clientId: environment.msalConfig.auth.clientId,
        authority: environment.msalConfig.auth.authority,
        redirectUri,
        postLogoutRedirectUri,
        navigateToLoginRequestUrl: false // Don't navigate back to original URL
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
        storeAuthStateInCookie: false
      },
      system: {
        loggerOptions: {
          loggerCallback,
          logLevel: environment.production ? LogLevel.Error : LogLevel.Info,
          piiLoggingEnabled: false
        },
        allowNativeBroker: false
      }
    });
  }
  return msalInstance;
}

/**
 * Get the MSAL instance (for use outside Angular DI)
 */
export function getMsalInstance(): PublicClientApplication | null {
  return msalInstance;
}

/**
 * MSAL Guard configuration for protected routes
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read', 'openid', 'profile', 'email']
    }
  };
}

/**
 * MSAL Interceptor configuration for API calls
 * Note: We don't use MSAL interceptor for our FastAPI backend,
 * we handle the token exchange manually in AuthService
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  // Only protect Microsoft Graph API calls
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/*', ['user.read']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}
